/**
 * AdempimentoInner — F-004 Phase 4 (IMPL-P4-002).
 *
 * Flusso di registrazione di un adempimento successivo (proroga | risoluzione |
 * cessione) montato come branch sibling di `CorrezionePraticaInner` /
 * `NuovaPraticaWizardInner` dentro `NuovaPraticaPage` (dispatch via query param
 * `?adempimentoMode=1&parentId=…`, INTRODUCTION.md Pattern 3 — nessuna nuova
 * `<Route>` in `App.tsx`).
 *
 * Estratto in `pages/` (convenzione "public surface = pages") per testabilità:
 * i test di pagina lo raggiungono via `NuovaPraticaPage` con `MemoryRouter`.
 * È tenuto come componente distinto da `NuovaPraticaWizardInner` /
 * `CorrezionePraticaInner` così gli hook (useForm, useEffect, useState) di un
 * flusso non vengono invocati nell'altro (React "rules of hooks" + isolamento).
 *
 * Orchestrazione (plan IMPL-P4-002):
 *  1. `AdempimentoTypeSelector` (step 0, sempre mostrato — nessun default
 *     naturale per la UX).
 *  2. `PraticaMadreReference` (stato A pre-risolto quando `parentId` è presente
 *     → fetch del detail madre → `prefillFromParent()` immediato).
 *  3. Il field group per-variante dentro `FormProvider` +
 *     `<PrefilledFieldsProvider value={prefilledPaths}>`, guidato da
 *     `useContractForm({ praticaId, schema: adempimentoDraftSchema,
 *     stepFields: ADEMPIMENTO_STEP_FIELDS[tipo] })` (dipendenza Phase 5).
 *  4. Lo step riepilogo/consenso riusa `SubmitStep` + `ResponsibilityCheckbox`,
 *     con `CessioneConfirmDialog` interposto prima del submit finale quando
 *     `tipo === 'CESSIONE'`.
 *  5. Creazione BOZZA via `createBozza({ tipologia: tipo, parentId })` una volta
 *     scelto il tipo (mirror della creazione mount-time di
 *     `NuovaPraticaWizardInner`, ma gated sulla scelta del tipo perché la
 *     `tipologia` deve essere nota al momento del create).
 *
 * Nota prefill (scope Phase 4): il prefill client-side legge i `datiStrutturati`
 * della madre via `fetchPraticaDetail(parentId)` (unico endpoint client-facing
 * che li espone). La ri-derivazione difensiva server-side dei campi immutabili
 * al submit è di Phase 2 (`rederiveImmutableFromParent`). Se il detail madre non
 * è recuperabile, il componente resta nello stato B/C (ricerca / estremi
 * manuali) senza prefill — nessun crash.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  adempimentoDraftSchema,
  prefillFromParent,
  type AdempimentoType,
  type ContractDraft,
} from '@app/shared';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useContractForm } from '@/features/pratica/hooks/useContractForm';
import { SubmitStep } from '@/features/pratica/components/wizard/SubmitStep';
import { createBozza, isPraticaApiError } from '@/features/pratica/lib/pratica-client';
import { fetchPraticaDetail } from '@/features/pratiche-cliente/lib/cliente-pratiche-client';
import { AdempimentoTypeSelector } from '../components/AdempimentoTypeSelector';
import {
  PraticaMadreReference,
  type ResolvedParentSummary,
} from '../components/PraticaMadreReference';
import { ProrogaFields } from '../components/fields/ProrogaFields';
import { RisoluzioneFields } from '../components/fields/RisoluzioneFields';
import { CessioneFields } from '../components/fields/CessioneFields';
import { CessioneConfirmDialog } from '../components/CessioneConfirmDialog';
import { PrefilledFieldsProvider } from '../hooks/usePrefilledFields';
import { ADEMPIMENTO_STEP_FIELDS } from '../lib/adempimento-step-fields';

export interface AdempimentoInnerProps {
  /** Pratica madre nota via query param (`?parentId=…`). Stato A pre-risolto. */
  parentId?: string;
}

/** Deriva un'etichetta immobile leggibile dai datiStrutturati della madre. */
function immobileLabel(dati: ContractDraft): string {
  const immobile = dati.immobile as { via?: string; comune?: string } | undefined;
  const parts = [immobile?.via, immobile?.comune].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

/** Deriva un'etichetta proprietario/locatore dai datiStrutturati della madre. */
function proprietarioLabel(dati: ContractDraft): string {
  const locatore = dati.locatore as
    | { nome?: string; cognome?: string }
    | undefined;
  const full = [locatore?.nome, locatore?.cognome].filter(Boolean).join(' ').trim();
  return full || '—';
}

/** ISO `YYYY-MM-DD` da un valore Date | stringa | undefined. */
function toIsoDate(raw: unknown): string | undefined {
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  if (typeof raw === 'string' && raw) return raw.slice(0, 10);
  return undefined;
}

export function AdempimentoInner({ parentId }: AdempimentoInnerProps) {
  const [tipo, setTipo] = useState<AdempimentoType | undefined>(undefined);

  // Pratica madre risolta (stato A) + relativi datiStrutturati per il prefill.
  const [resolvedSummary, setResolvedSummary] = useState<
    ResolvedParentSummary | undefined
  >(undefined);
  const [parentDati, setParentDati] = useState<ContractDraft | undefined>(undefined);
  const [parentError, setParentError] = useState<string | null>(null);

  // BOZZA adempimento (creata una volta scelto il tipo).
  const [praticaId, setPraticaId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  // Cessione: dialog di conferma prima del submit finale (WCAG 3.3.4).
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Pending state reale del submit (sorgente: SubmitStep via onSubmittingChange),
  // usato per disabilitare le azioni del dialog di conferma (TR-002).
  const [submitting, setSubmitting] = useState(false);
  // Resolver del gate `onBeforeSubmit`: il click su Conferma/Annulla del dialog
  // risolve la Promise attesa da `handleSubmit` in SubmitStep (fix TR-001).
  const confirmResolverRef = useRef<((proceed: boolean) => void) | null>(null);

  // ── Stato A: risoluzione della pratica madre quando `parentId` è noto. ──────
  useEffect(() => {
    if (!parentId) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await fetchPraticaDetail(parentId);
        if (cancelled) return;
        const dati = detail.pratica.datiStrutturati as unknown as ContractDraft;
        setParentDati(dati);
        setResolvedSummary({
          id: detail.pratica.id,
          codicePratica: detail.pratica.codicePratica,
          stato: detail.pratica.stato,
          immobileLabel: immobileLabel(dati),
          proprietarioLabel: proprietarioLabel(dati),
          parentDati: dati,
        });
      } catch {
        // Detail non recuperabile → resta in stato B/C senza prefill.
        if (!cancelled) {
          setParentError(
            'Impossibile caricare il contratto di riferimento. Cerca il contratto o inserisci gli estremi manualmente.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [parentId]);

  // ── BOZZA adempimento: creata quando il tipo è scelto (tipologia nota). ─────
  useEffect(() => {
    if (!tipo || praticaId) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await createBozza({ tipologia: tipo, parentId });
        if (!cancelled) setPraticaId(p.id);
      } catch (err) {
        if (!cancelled) {
          setCreateError(
            isPraticaApiError(err)
              ? err.message
              : 'Errore durante la creazione della bozza di adempimento.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tipo, parentId, praticaId]);

  // ── Prefill deterministico dei campi comuni quando madre + tipo sono noti. ──
  const prefill = useMemo(() => {
    if (!parentDati || !tipo) return undefined;
    return prefillFromParent(parentDati, tipo);
  }, [parentDati, tipo]);

  const prefilledPaths = prefill?.prefilledPaths ?? [];

  // Form adempimento (Phase 5 generalizzazione: schema + stepFields per variante).
  // Montato sempre a livello top per rispettare le rules of hooks; opera solo
  // quando `praticaId` è disponibile.
  const { form } = useContractForm({
    praticaId: praticaId ?? '',
    schema: adempimentoDraftSchema,
    stepFields: tipo ? ADEMPIMENTO_STEP_FIELDS[tipo] : [],
  });

  // Idratazione one-shot del form con i campi precompilati dalla madre.
  useEffect(() => {
    if (!prefill) return;
    form.reset(prefill.data as Parameters<typeof form.reset>[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const handleResolved = useCallback((dati: ContractDraft) => {
    setParentDati(dati);
  }, []);

  const handleChangeReference = useCallback(() => {
    setResolvedSummary(undefined);
    setParentDati(undefined);
  }, []);

  const scadenzaMinISO = parentDati ? toIsoDate(prefill?.data.dataScadenzaOriginale) : undefined;
  const stipulaMinISO = parentDati ? toIsoDate(prefill?.data.dataStipulaOriginale) : undefined;

  const conduttoreUscente =
    tipo === 'CESSIONE'
      ? (prefill?.data as { conduttoreUscente?: { nome?: string; cognome?: string; codiceFiscale?: string } } | undefined)
          ?.conduttoreUscente
      : undefined;

  // ── Gate di conferma cessione (fix TR-001) ──────────────────────────────────
  // Passato a SubmitStep come `onBeforeSubmit`. Viene `await`-ato da handleSubmit
  // PRIMA del submit reale:
  //  - PROROGA/RISOLUZIONE: `onBeforeSubmit` non è passato affatto → submit
  //    diretto, comportamento invariato.
  //  - CESSIONE: apre `CessioneConfirmDialog` e ritorna una Promise che risolve
  //    `true` su "Conferma cessione" (il submit prosegue) o `false` su "Annulla"
  //    (il submit è annullato). Nessun submit parte finché non si conferma.
  const handleBeforeCessioneSubmit = useCallback(
    (): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        confirmResolverRef.current = resolve;
        setConfirmOpen(true);
      }),
    [],
  );

  const resolveConfirm = useCallback((proceed: boolean) => {
    setConfirmOpen(false);
    const resolve = confirmResolverRef.current;
    confirmResolverRef.current = null;
    resolve?.(proceed);
  }, []);

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <Button
          variant="link"
          size="sm"
          asChild
          className="h-auto px-0 text-muted-foreground"
        >
          <Link to="/app/archivio">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Archivio
          </Link>
        </Button>
        <h1 className="text-3xl font-semibold tracking-tight">Registra adempimento</h1>
        <p className="text-muted-foreground">
          Proroga, risoluzione o cessione di un contratto già registrato.
        </p>
      </header>

      {createError && (
        <Alert variant="destructive" role="alert">
          {createError}
        </Alert>
      )}

      {parentError && (
        <Alert role="status" className="border-blue-300 text-blue-800">
          {parentError}
        </Alert>
      )}

      {/* Step 0 — tipo di adempimento (sempre mostrato). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle id="adempimento-type-heading" className="text-base">
            Tipo di adempimento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AdempimentoTypeSelector
            value={tipo}
            onChange={setTipo}
            aria-labelledby="adempimento-type-heading"
          />
        </CardContent>
      </Card>

      {/* Riferimento pratica madre (stato A/B/C). */}
      <PraticaMadreReference
        resolvedSummary={resolvedSummary}
        onResolved={handleResolved}
        onChangeReference={handleChangeReference}
      />

      {/* Campi per-variante (montati quando tipo + BOZZA sono pronti). */}
      {tipo && praticaId && (
        <FormProvider {...form}>
          <PrefilledFieldsProvider value={prefilledPaths}>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Dati dell&apos;adempimento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {tipo === 'PROROGA' && <ProrogaFields scadenzaMinISO={scadenzaMinISO} />}
                {tipo === 'RISOLUZIONE' && <RisoluzioneFields />}
                {tipo === 'CESSIONE' && (
                  <CessioneFields
                    conduttoreUscente={conduttoreUscente}
                    dataStipulaMinISO={stipulaMinISO}
                  />
                )}
              </CardContent>
            </Card>

            {/* Step riepilogo/consenso — riuso di SubmitStep (F-003/F-009/F-010).
                CESSIONE: `onBeforeSubmit` interpone la conferma esplicita PRIMA
                del submit reale (fix TR-001). PROROGA/RISOLUZIONE: nessun gate,
                submit diretto come F-003. `onSubmittingChange` porta il pending
                reale al dialog (fix TR-002). */}
            <SubmitStep
              praticaId={praticaId}
              onBeforeSubmit={
                tipo === 'CESSIONE' ? handleBeforeCessioneSubmit : undefined
              }
              onSubmittingChange={tipo === 'CESSIONE' ? setSubmitting : undefined}
              onSubmitted={() => {
                /* La navigazione post-submit è gestita da SubmitStep
                   (pagamento privati) / dal flusso submit standard. */
              }}
            />

            {/* Cessione: dialog di conferma prima del submit finale. `onConfirm`
                risolve il gate con `true` (SubmitStep prosegue al submit reale),
                `onCancel` con `false` (submit annullato). `isPending` riflette il
                pending reale del submit. */}
            {tipo === 'CESSIONE' && (
              <CessioneConfirmDialog
                open={confirmOpen}
                uscenteName={[conduttoreUscente?.nome, conduttoreUscente?.cognome]
                  .filter(Boolean)
                  .join(' ')}
                uscenteCF={conduttoreUscente?.codiceFiscale ?? ''}
                nuovoName={[
                  (form.getValues() as { nuovoInquilino?: { nome?: string; cognome?: string } })
                    .nuovoInquilino?.nome,
                  (form.getValues() as { nuovoInquilino?: { nome?: string; cognome?: string } })
                    .nuovoInquilino?.cognome,
                ]
                  .filter(Boolean)
                  .join(' ')}
                nuovoCF={
                  (form.getValues() as { nuovoInquilino?: { codiceFiscale?: string } })
                    .nuovoInquilino?.codiceFiscale ?? ''
                }
                dataDecorrenza={
                  toIsoDate((form.getValues() as { dataDecorrenza?: unknown }).dataDecorrenza) ?? ''
                }
                isPending={submitting}
                onConfirm={() => resolveConfirm(true)}
                onCancel={() => resolveConfirm(false)}
              />
            )}
          </PrefilledFieldsProvider>
        </FormProvider>
      )}
    </section>
  );
}
